// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  Monitor, 
  Edit3, 
  Clock, 
  Image, 
  TrendingUp, 
  Wifi, 
  WifiOff,
  Play,
  Pause,
  Settings,
  MoreVertical,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Données de démonstration
  const stats = [
    {
      title: 'Écrans Connectés',
      value: '2/3',
      change: '0',
      icon: Monitor,
      color: 'green',
      description: '1 écran hors ligne'
    },
    {
      title: 'Templates Actifs',
      value: '5',
      change: '+2',
      icon: Edit3,
      color: 'blue',
      description: 'Depuis la semaine dernière'
    },
    {
      title: 'Médias Stockés',
      value: '47',
      change: '+8',
      icon: Image,
      color: 'purple',
      description: '245 MB utilisés'
    },
    {
      title: 'Règles Programmées',
      value: '12',
      change: '+3',
      icon: Clock,
      color: 'orange',
      description: '8 actives actuellement'
    }
  ];

  const displays = [
    {
      id: 'display-1',
      name: 'Écran Principal - Comptoir',
      status: 'online',
      location: 'Comptoir principal',
      currentTemplate: 'Menu Petit-déjeuner',
      lastUpdate: '2 min',
      resolution: '1920x1080',
      uptime: '99.8%'
    },
    {
      id: 'display-2',
      name: 'Écran Vitrine',
      status: 'online',
      location: 'Vitrine extérieure',
      currentTemplate: 'Promotions du jour',
      lastUpdate: '5 min',
      resolution: '1080x1920',
      uptime: '97.2%'
    },
    {
      id: 'display-3',
      name: 'Écran Terrasse',
      status: 'offline',
      location: 'Terrasse',
      currentTemplate: null,
      lastUpdate: '2h 15min',
      resolution: '1920x1080',
      uptime: '85.4%'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'template_updated',
      message: 'Template "Menu Petit-déjeuner" mis à jour',
      time: '5 minutes',
      user: 'Admin'
    },
    {
      id: 2,
      type: 'display_offline',
      message: 'Écran Terrasse hors ligne',
      time: '2 heures',
      user: 'Système'
    },
    {
      id: 3,
      type: 'media_uploaded',
      message: '3 nouveaux médias ajoutés',
      time: '3 heures',
      user: 'Admin'
    },
    {
      id: 4,
      type: 'schedule_created',
      message: 'Nouvelle règle "Menu Week-end" créée',
      time: '1 jour',
      user: 'Admin'
    }
  ];

  const StatCard = ({ stat }) => {
    const Icon = stat.icon;
    const colorClasses = {
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600'
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
            <div className="flex items-baseline space-x-2 mt-1">
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              {stat.change !== '0' && (
                <span className={`text-sm font-medium ${
                  stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </div>
    );
  };

  const DisplayCard = ({ display }) => (
    <div className="bg-white p-6 rounded-lg shadow border">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            display.status === 'online' ? 'bg-green-500' : 'bg-red-500'
          }`} />
          <div>
            <h3 className="font-semibold text-gray-900">{display.name}</h3>
            <p className="text-sm text-gray-600">{display.location}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {display.status === 'online' ? (
            <Wifi className="h-4 w-4 text-green-600" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-600" />
          )}
          <button className="p-1 hover:bg-gray-100 rounded">
            <MoreVertical className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Template actuel:</span>
          <span className="text-sm font-medium">
            {display.currentTemplate || 'Aucun'}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Dernière maj:</span>
          <span className="text-sm text-gray-900">{display.lastUpdate}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Résolution:</span>
          <span className="text-sm text-gray-900">{display.resolution}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Uptime:</span>
          <span className={`text-sm font-medium ${
            parseFloat(display.uptime) > 95 ? 'text-green-600' : 'text-orange-600'
          }`}>
            {display.uptime}
          </span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t">
        <div className="flex space-x-2">
          <button className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm flex items-center justify-center space-x-1">
            <Settings className="h-3 w-3" />
            <span>Configurer</span>
          </button>
          {display.status === 'online' ? (
            <button className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-sm flex items-center justify-center space-x-1">
              <Play className="h-3 w-3" />
              <span>Voir</span>
            </button>
          ) : (
            <button className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm flex items-center justify-center space-x-1">
              <AlertTriangle className="h-3 w-3" />
              <span>Diagnostique</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            {currentTime.toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} - {currentTime.toLocaleTimeString('fr-FR')}
          </p>
        </div>
        
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Actualiser
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Nouveau Template
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} stat={stat} />
        ))}
      </div>

      {/* Contenu principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* État des écrans */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow border">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">État des Écrans</h2>
              <p className="text-sm text-gray-600">Monitoring en temps réel</p>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {displays.map(display => (
                <DisplayCard key={display.id} display={display} />
              ))}
            </div>
          </div>
        </div>

        {/* Activité récente */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Activité Récente</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map(activity => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {activity.type === 'display_offline' ? (
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2" />
                      ) : (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <p className="text-xs text-gray-500">il y a {activity.time}</p>
                        <span className="text-xs text-gray-400">•</span>
                        <p className="text-xs text-gray-500">{activity.user}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Actions Rapides</h2>
            </div>
            <div className="p-6 space-y-3">
              <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Edit3 className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-sm">Créer un template</div>
                  <div className="text-xs text-gray-600">Nouveau design d'affichage</div>
                </div>
              </button>

              <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Image className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <div className="font-medium text-sm">Ajouter des médias</div>
                  <div className="text-xs text-gray-600">Images et vidéos</div>
                </div>
              </button>

              <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-sm">Programmer du contenu</div>
                  <div className="text-xs text-gray-600">Nouvelles règles</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;